import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Container, Paper, Tooltip, Typography,
  useMediaQuery, useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35", greenBd: "#a8d5ac",
  redBg: "#fdf0ef", redTx: "#9b2c2c", redBd: "#f0a8a8",
  blueBg: "#eef4fd", blueTx: "#1e3f7a",
  peach: "#ffebd3", inputBg: "#f8f7f5",
};

/* ═══════════════════════════════════════════════════════════════════════════
   HALLS & LEDs
   ═══════════════════════════════════════════════════════════════════════════ */
const HALLS = [
  { key: "Nova",    capacity: 250, color: "#1565c0", bg: "#e8f0fe" },
  { key: "Titan",   capacity: 280, color: "#2e7d32", bg: "#e6f4ea" },
  { key: "Lunar",   capacity: 260, color: "#e65100", bg: "#fff3e0" },
  { key: "Eclipse", capacity: 250, color: "#6a1b9a", bg: "#f3e5f5" },
];

const LEDS = [
  { id: 1, name: "LED 1", description: "Main stage left screen", price: 3000 },
  { id: 2, name: "LED 2", description: "Main stage right screen", price: 3000 },
  { id: 3, name: "LED 3", description: "Side panel screen", price: 1800 },
  { id: 4, name: "LED 4", description: "Rear projection screen", price: 1800 },
];

/* ═══════════════════════════════════════════════════════════════════════════
   BUSINESS RULES ENGINE
   ═══════════════════════════════════════════════════════════════════════════ */
interface LEDStatus {
  state: "included" | "available" | "disabled";
  reason: string;
}

function getLEDStatus(ledName: string, selectedHalls: string[]): LEDStatus {
  const hasNova    = selectedHalls.includes("Nova");
  const hasEclipse = selectedHalls.includes("Eclipse");
  const hasTitan   = selectedHalls.includes("Titan");
  const hasLunar   = selectedHalls.includes("Lunar");

  if (selectedHalls.length === 0) {
    return { state: "disabled", reason: "Select a hall first" };
  }

  // LED 1 rules
  if (ledName === "LED 1") {
    if (hasNova) return { state: "included", reason: "Included with Nova Hall" };
    if (hasTitan || hasLunar) return { state: "disabled", reason: "Not available for Titan/Lunar" };
    return { state: "disabled", reason: "Only available with Nova Hall" };
  }

  // LED 2 rules
  if (ledName === "LED 2") {
    if (hasEclipse) return { state: "included", reason: "Included with Eclipse Hall" };
    if (hasTitan || hasLunar) return { state: "disabled", reason: "Not available for Titan/Lunar" };
    return { state: "disabled", reason: "Only available with Eclipse Hall" };
  }

  // LED 3 & LED 4 — always available when any hall selected
  return { state: "available", reason: "Available for all halls" };
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const LEDScreensPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedHalls, setSelectedHalls] = useState<string[]>([]);
  const [selectedLEDs, setSelectedLEDs]   = useState<string[]>([]);

  /* ── Auto-assign LEDs when halls change ── */
  useEffect(() => {
    setSelectedLEDs(prev => {
      const auto: string[] = [];
      if (selectedHalls.includes("Nova")) auto.push("LED 1");
      if (selectedHalls.includes("Eclipse")) auto.push("LED 2");

      // Keep manual selections that are still valid
      const manual = prev.filter(led => {
        if (auto.includes(led)) return false; // already auto
        const status = getLEDStatus(led, selectedHalls);
        return status.state === "available";
      });

      return [...auto, ...manual];
    });
  }, [selectedHalls]);

  /* ── Hall toggle ── */
  const toggleHall = (key: string) => {
    setSelectedHalls(prev =>
      prev.includes(key) ? prev.filter(h => h !== key) : [...prev, key],
    );
  };

  /* ── LED toggle (manual only) ── */
  const toggleLED = (ledName: string) => {
    const status = getLEDStatus(ledName, selectedHalls);
    if (status.state !== "available") return;
    setSelectedLEDs(prev =>
      prev.includes(ledName) ? prev.filter(l => l !== ledName) : [...prev, ledName],
    );
  };

  /* ── Computed summary ── */
  const summary = useMemo(() => {
    const included = LEDS.filter(l => {
      const s = getLEDStatus(l.name, selectedHalls);
      return s.state === "included";
    });
    const manual = LEDS.filter(l => selectedLEDs.includes(l.name) && getLEDStatus(l.name, selectedHalls).state === "available");
    const all = [...included, ...manual];
    const totalCost = manual.reduce((s, l) => s + l.price, 0); // included = free
    return { included, manual, all, totalCost };
  }, [selectedHalls, selectedLEDs]);

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 } }}>
      <Box sx={{ pb: { xs: 10, sm: 8 } }}>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 0.5 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: "10px", background: `linear-gradient(135deg, ${T.gold}, #a07840)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TvOutlinedIcon sx={{ fontSize: 18, color: T.navy }} />
            </Box>
            <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>
              LED Screens Service
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: T.slateLt }}>
            Select halls to see available LED screens. Some screens are automatically included with specific halls.
          </Typography>
        </Box>

        {/* ══════════ INTERACTIVE HALL LAYOUT ══════════ */}
        <Paper elevation={0} sx={{ bgcolor: T.white, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.02)", mb: 3, p: { xs: 2, md: 3 }, overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <MeetingRoomOutlinedIcon sx={{ fontSize: 17, color: T.gold }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }}>Hall & LED Layout</Typography>
            <Typography sx={{ fontSize: 11, color: T.slateLt, ml: 1 }}>Click halls to select, LEDs update automatically</Typography>
          </Box>

          <Box sx={{ width: "100%", maxWidth: 800, mx: "auto" }}>
            <svg viewBox="0 0 800 340" style={{ width: "100%", height: "auto" }}>
              <defs>
                {/* Glow filter for active LEDs */}
                <filter id="glow-green"><feGaussianBlur stdDeviation="4" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                <filter id="glow-gold"><feGaussianBlur stdDeviation="4" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
              </defs>

              {/* Background */}
              <rect x="0" y="0" width="800" height="340" rx="16" fill={T.bg} />

              {/* Stage label */}
              <rect x="280" y="8" width="240" height="24" rx="12" fill={T.navy} />
              <text x="400" y="24" textAnchor="middle" fill={T.gold} fontSize="10" fontWeight="600" fontFamily="sans-serif">STAGE AREA</text>

              {/* ── HALLS ── */}
              {[
                { key: "Nova",    x: 40,  y: 50, w: 170, h: 130, color: "#1565c0", bg: "#e8f0fe" },
                { key: "Titan",   x: 220, y: 50, w: 170, h: 130, color: "#2e7d32", bg: "#e6f4ea" },
                { key: "Lunar",   x: 410, y: 50, w: 170, h: 130, color: "#e65100", bg: "#fff3e0" },
                { key: "Eclipse", x: 590, y: 50, w: 170, h: 130, color: "#6a1b9a", bg: "#f3e5f5" },
              ].map(h => {
                const sel = selectedHalls.includes(h.key);
                const cap = HALLS.find(x => x.key === h.key)?.capacity ?? 0;
                return (
                  <g key={h.key} onClick={() => toggleHall(h.key)} style={{ cursor: "pointer" }}>
                    {/* Shadow */}
                    {sel && <rect x={h.x - 2} y={h.y - 2} width={h.w + 4} height={h.h + 4} rx="14" fill={h.color} opacity="0.15" />}
                    {/* Hall box */}
                    <rect x={h.x} y={h.y} width={h.w} height={h.h} rx="12"
                      fill={sel ? h.bg : T.white}
                      stroke={sel ? h.color : T.line}
                      strokeWidth={sel ? 2.5 : 1.5}
                    />
                    {/* Initial circle */}
                    <circle cx={h.x + h.w / 2} cy={h.y + 40} r="22"
                      fill={sel ? `${h.color}20` : T.bg}
                      stroke={sel ? `${h.color}50` : T.line}
                      strokeWidth="1.5"
                    />
                    <text x={h.x + h.w / 2} y={h.y + 45} textAnchor="middle" fill={sel ? h.color : T.slateLt} fontSize="18" fontWeight="800" fontFamily="sans-serif">
                      {h.key.charAt(0)}
                    </text>
                    {/* Name */}
                    <text x={h.x + h.w / 2} y={h.y + 80} textAnchor="middle" fill={sel ? h.color : T.navy} fontSize="14" fontWeight="700" fontFamily="sans-serif">
                      {h.key}
                    </text>
                    {/* Capacity */}
                    <text x={h.x + h.w / 2} y={h.y + 98} textAnchor="middle" fill={T.slateLt} fontSize="10" fontFamily="sans-serif">
                      {cap} guests
                    </text>
                    {/* Checkmark */}
                    {sel && (
                      <g>
                        <circle cx={h.x + h.w - 14} cy={h.y + 14} r="10" fill={h.color} />
                        <path d={`M${h.x + h.w - 19} ${h.y + 14} l3 3 l6 -6`} stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                    )}
                  </g>
                );
              })}

              {/* ── CONNECTION LINES ── */}
              {(() => {
                const lines: React.ReactNode[] = [];
                const hasNova = selectedHalls.includes("Nova");
                const hasEclipse = selectedHalls.includes("Eclipse");
                const hasTitan = selectedHalls.includes("Titan");
                const hasLunar = selectedHalls.includes("Lunar");

                // Nova → LED 1
                if (hasNova) {
                  lines.push(
                    <line key="c1" x1="40" y1="115" x2="24" y2="230" stroke={T.greenTx} strokeWidth="2" strokeDasharray="6 3" opacity="0.5" />,
                  );
                }
                // Eclipse → LED 2
                if (hasEclipse) {
                  lines.push(
                    <line key="c2" x1="760" y1="115" x2="776" y2="230" stroke={T.greenTx} strokeWidth="2" strokeDasharray="6 3" opacity="0.5" />,
                  );
                }
                // Titan → LED 3
                if (hasTitan || hasLunar) {
                  const isActive3 = selectedLEDs.includes("LED 3");
                  const isActive4 = selectedLEDs.includes("LED 4");
                  if (isActive3) lines.push(<line key="c3" x1="305" y1="180" x2="260" y2="260" stroke={T.gold} strokeWidth="2" strokeDasharray="6 3" opacity="0.5" />);
                  if (isActive4) lines.push(<line key="c4" x1="495" y1="180" x2="540" y2="260" stroke={T.gold} strokeWidth="2" strokeDasharray="6 3" opacity="0.5" />);
                }
                return lines;
              })()}

              {/* ── LED SCREENS ── */}
              {[
                { name: "LED 1", cx: 24,  cy: 260 },
                { name: "LED 2", cx: 776, cy: 260 },
                { name: "LED 3", cx: 260, cy: 290 },
                { name: "LED 4", cx: 540, cy: 290 },
              ].map(led => {
                const status = getLEDStatus(led.name, selectedHalls);
                const isSelected = selectedLEDs.includes(led.name);
                const isActive = status.state === "included" || isSelected;

                const fillColor =
                  status.state === "included" ? T.greenTx
                  : isSelected ? T.gold
                  : status.state === "disabled" ? T.slateLt
                  : T.slate;

                const bgColor =
                  status.state === "included" ? T.greenBg
                  : isSelected ? T.peach
                  : status.state === "disabled" ? "#f0f0ee"
                  : T.white;

                return (
                  <g key={led.name}
                    onClick={() => status.state === "available" && toggleLED(led.name)}
                    style={{ cursor: status.state === "available" ? "pointer" : "default" }}
                    opacity={status.state === "disabled" ? 0.4 : 1}
                  >
                    <Tooltip title={status.reason} arrow>
                      <g>
                        {/* Glow ring for active */}
                        {isActive && <circle cx={led.cx} cy={led.cy} r="28" fill="none" stroke={fillColor} strokeWidth="1" opacity="0.3" />}

                        {/* LED circle */}
                        <circle cx={led.cx} cy={led.cy} r="22"
                          fill={bgColor} stroke={fillColor} strokeWidth={isActive ? 2.5 : 1.5}
                          filter={isActive ? (status.state === "included" ? "url(#glow-green)" : "url(#glow-gold)") : undefined}
                        />

                        {/* TV icon (simplified) */}
                        <rect x={led.cx - 8} y={led.cy - 7} width="16" height="10" rx="2" fill="none" stroke={fillColor} strokeWidth="1.5" />
                        <line x1={led.cx - 4} y1={led.cy + 5} x2={led.cx + 4} y2={led.cy + 5} stroke={fillColor} strokeWidth="1.5" strokeLinecap="round" />

                        {/* Lock icon for included */}
                        {status.state === "included" && (
                          <g>
                            <circle cx={led.cx + 14} cy={led.cy - 14} r="7" fill={T.greenTx} />
                            <rect x={led.cx + 11} y={led.cy - 16} width="6" height="5" rx="1" fill="#fff" />
                            <path d={`M${led.cx + 12.5} ${led.cy - 16} v-2 a1.5 1.5 0 0 1 3 0 v2`} fill="none" stroke="#fff" strokeWidth="1" />
                          </g>
                        )}

                        {/* Name label */}
                        <text x={led.cx} y={led.cy + 40} textAnchor="middle" fill={fillColor} fontSize="11" fontWeight="600" fontFamily="sans-serif">
                          {led.name}
                        </text>

                        {/* Price / status label */}
                        <text x={led.cx} y={led.cy + 52} textAnchor="middle" fill={T.slateLt} fontSize="9" fontFamily="sans-serif">
                          {status.state === "included" ? "Free" : status.state === "disabled" ? "N/A" : `$${LEDS.find(l => l.name === led.name)?.price ?? 0}`}
                        </text>
                      </g>
                    </Tooltip>
                  </g>
                );
              })}

              {/* Legend */}
              <g transform="translate(260, 320)">
                <circle cx="0" cy="0" r="4" fill={T.greenTx} />
                <text x="8" y="3" fill={T.slateLt} fontSize="9" fontFamily="sans-serif">Included</text>
                <circle cx="80" cy="0" r="4" fill={T.gold} />
                <text x="88" y="3" fill={T.slateLt} fontSize="9" fontFamily="sans-serif">Selected</text>
                <circle cx="155" cy="0" r="4" fill={T.slateLt} />
                <text x="163" y="3" fill={T.slateLt} fontSize="9" fontFamily="sans-serif">Disabled</text>
              </g>
            </svg>
          </Box>
        </Paper>

        <Box sx={{ display: "flex", gap: { xs: 3, md: 4 }, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>

          {/* ══════════ LEFT — Configuration ══════════ */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* ── Section 1: Hall Selection ── */}
            <Paper elevation={0} sx={{ bgcolor: T.white, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.02)", mb: 3, overflow: "hidden" }}>
              <Box sx={{ px: { xs: 2.5, md: 3 }, pt: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MeetingRoomOutlinedIcon sx={{ fontSize: 17, color: T.gold }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }}>Select Halls</Typography>
                  {selectedHalls.length > 0 && (
                    <Chip label={`${selectedHalls.length} selected`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: T.peach, color: T.navy, fontWeight: 600 }} />
                  )}
                </Box>
              </Box>
              <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
                  {HALLS.map(hall => {
                    const selected = selectedHalls.includes(hall.key);
                    return (
                      <Box key={hall.key} onClick={() => toggleHall(hall.key)}
                        sx={{
                          position: "relative", borderRadius: "14px", overflow: "hidden",
                          cursor: "pointer", transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                          border: `2px solid ${selected ? hall.color : "transparent"}`,
                          boxShadow: selected ? `0 0 0 1px ${hall.color}, 0 4px 16px ${hall.color}22` : "0 1px 3px rgba(0,0,0,0.04)",
                          bgcolor: selected ? hall.bg : T.white,
                          "&:hover": { transform: "translateY(-2px)", boxShadow: selected ? `0 0 0 1px ${hall.color}, 0 8px 24px ${hall.color}28` : "0 4px 12px rgba(0,0,0,0.06)" },
                        }}>
                        <Box sx={{ height: 4, bgcolor: selected ? hall.color : T.line }} />
                        <Box sx={{ px: 2, pt: 2, pb: 2, textAlign: "center" }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: "10px", mx: "auto", mb: 1, bgcolor: selected ? `${hall.color}18` : T.bg, border: `1.5px solid ${selected ? `${hall.color}40` : T.line}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: selected ? hall.color : T.slateLt }}>{hall.key.charAt(0)}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: selected ? hall.color : T.navy }}>{hall.key}</Typography>
                          <Typography sx={{ fontSize: 11, color: T.slateLt }}>{hall.capacity} guests</Typography>
                        </Box>
                        {selected && (
                          <Box sx={{ position: "absolute", top: 8, right: 8, width: 20, height: 20, borderRadius: "50%", bgcolor: hall.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <CheckCircleIcon sx={{ fontSize: 14, color: T.white }} />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Paper>

            {/* ── Section 2: LED Screens ── */}
            <Paper elevation={0} sx={{ bgcolor: T.white, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.02)", overflow: "hidden" }}>
              <Box sx={{ px: { xs: 2.5, md: 3 }, pt: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TvOutlinedIcon sx={{ fontSize: 17, color: T.gold }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }}>LED Screens</Typography>
                  {selectedLEDs.length > 0 && (
                    <Chip label={`${selectedLEDs.length} active`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: T.greenBg, color: T.greenTx, fontWeight: 600 }} />
                  )}
                </Box>
              </Box>
              <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
                {selectedHalls.length === 0 && (
                  <Box sx={{ bgcolor: T.bg, borderRadius: "12px", p: 3, textAlign: "center" }}>
                    <MeetingRoomOutlinedIcon sx={{ fontSize: 36, color: T.line, mb: 1 }} />
                    <Typography sx={{ fontSize: 13, color: T.slateLt }}>Select one or more halls above to see available LED screens</Typography>
                  </Box>
                )}

                {selectedHalls.length > 0 && (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
                    {LEDS.map(led => {
                      const status = getLEDStatus(led.name, selectedHalls);
                      const isSelected = selectedLEDs.includes(led.name);

                      return (
                        <Tooltip key={led.id} title={status.reason} arrow>
                          <Paper elevation={0}
                            onClick={() => status.state === "available" && toggleLED(led.name)}
                            sx={{
                              p: 2.5, borderRadius: "14px", cursor: status.state === "available" ? "pointer" : "default",
                              transition: "all 0.2s",
                              border: `2px solid ${
                                status.state === "included" ? T.greenBd
                                : isSelected ? T.gold
                                : status.state === "disabled" ? "transparent"
                                : T.line
                              }`,
                              bgcolor:
                                status.state === "included" ? T.greenBg
                                : isSelected ? T.peach
                                : status.state === "disabled" ? "#fafaf8"
                                : T.white,
                              opacity: status.state === "disabled" ? 0.5 : 1,
                              "&:hover": status.state === "available" ? {
                                borderColor: T.gold,
                                bgcolor: isSelected ? T.peach : "#fef6ee",
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                              } : {},
                            }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
                              {/* LED icon + name */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Box sx={{
                                  width: 40, height: 40, borderRadius: "10px",
                                  bgcolor:
                                    status.state === "included" ? `${T.greenTx}15`
                                    : isSelected ? `${T.gold}20`
                                    : T.bg,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                  <TvOutlinedIcon sx={{
                                    fontSize: 20,
                                    color:
                                      status.state === "included" ? T.greenTx
                                      : isSelected ? T.gold
                                      : status.state === "disabled" ? T.slateLt
                                      : T.slate,
                                  }} />
                                </Box>
                                <Box>
                                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy }}>{led.name}</Typography>
                                  <Typography sx={{ fontSize: 11, color: T.slateLt }}>{led.description}</Typography>
                                </Box>
                              </Box>

                              {/* Status badge */}
                              {status.state === "included" && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: T.greenBg, border: `1px solid ${T.greenBd}`, borderRadius: "8px", px: 1, py: "3px" }}>
                                  <LockOutlinedIcon sx={{ fontSize: 12, color: T.greenTx }} />
                                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.greenTx }}>Included</Typography>
                                </Box>
                              )}
                              {status.state === "disabled" && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: T.redBg, border: `1px solid ${T.redBd}`, borderRadius: "8px", px: 1, py: "3px" }}>
                                  <BlockOutlinedIcon sx={{ fontSize: 12, color: T.redTx }} />
                                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.redTx }}>Unavailable</Typography>
                                </Box>
                              )}
                              {status.state === "available" && isSelected && (
                                <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: T.gold, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <CheckCircleIcon sx={{ fontSize: 16, color: T.white }} />
                                </Box>
                              )}
                            </Box>

                            {/* Price + reason */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <InfoOutlinedIcon sx={{ fontSize: 12 }} /> {status.reason}
                              </Typography>
                              <Typography sx={{
                                fontSize: 16, fontWeight: 700, lineHeight: 1,
                                color: status.state === "included" ? T.greenTx : status.state === "disabled" ? T.slateLt : T.navy,
                              }}>
                                {status.state === "included" ? "Free" : `$${led.price}`}
                              </Typography>
                            </Box>
                          </Paper>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* ══════════ RIGHT — Summary Panel ══════════ */}
          <Box sx={{ width: { xs: "100%", md: 340 }, flexShrink: 0, position: { md: "sticky" }, top: { md: 100 } }}>
            <Paper elevation={0} sx={{ bgcolor: T.white, borderRadius: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.03)", p: 3 }}>

              {/* Header */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.1em", mb: 2 }}>
                Configuration Summary
              </Typography>

              {/* Selected halls */}
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.8 }}>Halls</Typography>
              {selectedHalls.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: T.slateLt, mb: 2 }}>No halls selected</Typography>
              ) : (
                <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mb: 2 }}>
                  {selectedHalls.map(h => {
                    const hall = HALLS.find(x => x.key === h)!;
                    return <Chip key={h} label={h} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, color: hall.color, bgcolor: hall.bg, border: `1px solid ${hall.color}25` }} />;
                  })}
                </Box>
              )}

              {/* Active LEDs */}
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.8 }}>Active Screens</Typography>
              {summary.all.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: T.slateLt, mb: 2 }}>No screens active</Typography>
              ) : (
                <Box sx={{ mb: 2 }}>
                  {summary.all.map(led => {
                    const st = getLEDStatus(led.name, selectedHalls);
                    return (
                      <Box key={led.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: "5px", borderBottom: `1px solid ${T.line}`, "&:last-child": { borderBottom: "none" } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          <TvOutlinedIcon sx={{ fontSize: 14, color: st.state === "included" ? T.greenTx : T.gold }} />
                          <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy }}>{led.name}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: st.state === "included" ? T.greenTx : T.navy }}>
                          {st.state === "included" ? "Free" : `$${led.price}`}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Divider */}
              <Box sx={{ height: 1, bgcolor: T.line, my: 2 }} />

              {/* Breakdown */}
              <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
                <Typography sx={{ fontSize: 12, color: T.slateLt }}>Included screens</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.greenTx }}>{summary.included.length} (free)</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
                <Typography sx={{ fontSize: 12, color: T.slateLt }}>Additional screens</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy }}>{summary.manual.length}</Typography>
              </Box>

              {/* Total */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, py: 1.5, px: 2, bgcolor: T.peach, borderRadius: "12px" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>Additional Cost</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.navy, lineHeight: 1 }}>${summary.totalCost}</Typography>
              </Box>

              {/* Rules hint */}
              <Box sx={{ mt: 2, p: 1.5, bgcolor: T.bg, borderRadius: "10px" }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Rules</Typography>
                <Typography sx={{ fontSize: 11, color: T.slateLt, lineHeight: 1.6 }}>
                  Nova → LED 1 included free<br />
                  Eclipse → LED 2 included free<br />
                  Titan / Lunar → LED 3 & 4 only<br />
                  LED 1 & 2 not available for Titan/Lunar
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default LEDScreensPage;
